import React, { useState, useRef, useEffect, useMemo } from 'react'
import { getImageUrl } from '../utils/cdnUtils'
import { lotteryService, type LotteryRound, type BetSlip } from '../services/lotteryService'
import { numberSetService, type CustomerNumberSet } from '../services/numberSetService'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

interface BettingPackage {
  code: string
  name: string
  packageType: 'RATE_BASED' | 'SET_BASED'
  prizeConfigs?: Array<{
    prizeTypeCode: string
    prizeTypeName: string
    payoutRate: number
    discount: number
    minBet?: number
    maxBet?: number
  }>
  setConfig?: {
    fixedPrice: number
    fixedPayout: number
    discount: number
  }
  [key: string]: any
}

interface BetItem {
  id: string
  number: string
  betType: string
  betTypeLabel: string
  amount: number
  multiplier: number
  discount: number
  payout: number
}

interface LotteryBetSideLeftProps {
  selectedBetType: string
  setSelectedBetType: (betType: string) => void
  numbers: string[]
  handleNumberInput: (index: number, value: string) => void
  addBetItem?: (betData: any) => void
  round?: LotteryRound
  roundImage?: string
  selectedPackage?: BettingPackage
  betItems?: BetItem[]
}

const LotteryBetSideLeft: React.FC<LotteryBetSideLeftProps> = ({
  selectedBetType: _selectedBetType,
  setSelectedBetType: _setSelectedBetType,
  numbers: _numbers,
  handleNumberInput: _handleNumberInput,
  addBetItem,
  round,
  roundImage,
  selectedPackage,
  betItems = []
}) => {
  // State สำหรับจัดการการเลือกประเภทการแทง
  const [selectedPrizeCodes, setSelectedPrizeCodes] = useState<string[]>([])
  const [selectedPickNumbers, setSelectedPickNumbers] = useState<string[]>([])
  const [special2DigitType, setSpecial2DigitType] = useState<string | null>(null)
  const [showNumberPanel, setShowNumberPanel] = useState<boolean>(false)
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<number | null>(null)
  const [countdown, setCountdown] = useState<string>('')
  const [showNumberSetModal, setShowNumberSetModal] = useState(false)
  const [numberSetList, setNumberSetList] = useState<CustomerNumberSet[]>([])
  const [loadingNumberSets, setLoadingNumberSets] = useState(false)
  const [showOldSlipModal, setShowOldSlipModal] = useState(false)
  const [oldSlipList, setOldSlipList] = useState<BetSlip[]>([])
  const [loadingOldSlips, setLoadingOldSlips] = useState(false)
  const [showOnlyCurrentPackage, setShowOnlyCurrentPackage] = useState(false)

  // Extract เลขที่ถูกเลือกไว้แล้วจาก betItems เพื่อแสดง highlight ในแผงตัวเลข
  const selectedNumbersFromBetItems = useMemo(() => {
    if (!betItems || betItems.length === 0) return new Set<string>()
    
    // สร้าง Set ของเลขที่ถูกเลือกไว้แล้ว (ไม่ซ้ำ)
    const numbersSet = new Set<string>()
    betItems.forEach(item => {
      if (item.number) {
        numbersSet.add(item.number)
      }
    })
    return numbersSet
  }, [betItems])

  // หวยยี่กี: ดึงโพยเก่าไม่แยกประเภทหวย/รอบ — โหลดโพยทั้งหมดมาใช้แทงได้
  const isYeekee = useMemo(() => {
    if (!round) return false
    const huayTypeName = (round as any).huayTypeName || ''
    const huayName = (round as any).huayName || ''
    return /ยี่กี|ยี/.test(huayTypeName) || /ยี่กี|ยี/.test(huayName)
  }, [round])

  const closeTime = useMemo(() => {
    if (!round) return null
    const betEndDate = new Date(round.betEndTime)
    return betEndDate.toLocaleString('th-TH', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }, [round])

  useEffect(() => {
    if (!round) {
      setCountdown('--:--:--')
      return
    }

    const updateCountdown = () => {
      setCountdown(lotteryService.formatTimeRemaining(round.betEndTime))
    }

    updateCountdown()
    const intervalId = setInterval(updateCountdown, 1000)

    return () => clearInterval(intervalId)
  }, [round])

  // โหลดรายการเลขชุดเมื่อเปิด modal
  useEffect(() => {
    if (!showNumberSetModal) return
    let cancelled = false
    setLoadingNumberSets(true)
    numberSetService
      .list()
      .then(list => {
        if (!cancelled) setNumberSetList(list)
      })
      .catch(() => {
        if (!cancelled) {
          setNumberSetList([])
          toast.error('โหลดรายการเลขชุดไม่สำเร็จ')
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingNumberSets(false)
      })
    return () => { cancelled = true }
  }, [showNumberSetModal])

  // โหลดโพยเก่าเมื่อเปิด modal ดึงโพยเก่า
  useEffect(() => {
    if (!showOldSlipModal) return
    let cancelled = false
    const run = async () => {
      try {
        const token = localStorage.getItem('accessToken')
        if (!token) {
          if (!cancelled) setOldSlipList([])
          return
        }
        const payload = JSON.parse(atob(token.split('.')[1]))
        const userId = payload.sub || payload.id || payload.userId
        if (!userId) {
          if (!cancelled) setOldSlipList([])
          return
        }
        setLoadingOldSlips(true)
        const opts: { limit: number; isSettled: boolean; roundId?: string } = {
          limit: 30,
          isSettled: false
        }
        if (!isYeekee && round?._id) opts.roundId = round._id
        const { slips } = await lotteryService.getUserSlips(userId, opts)
        if (!cancelled) setOldSlipList(slips || [])
      } catch {
        if (!cancelled) setOldSlipList([])
      } finally {
        if (!cancelled) setLoadingOldSlips(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [showOldSlipModal, round?._id, isYeekee])

  // สร้างเลขสลับสำหรับ 2 หลัก [ab → ab, ba] หรือ 3 หลัก [abc → 6 ชุด]
  const expandReverseNumbers = (num: string, prizeCode: string): string[] => {
    if (prizeCode === 'tod3reverse' && num.length === 3) {
      const chars = num.split('')
      const set = new Set<string>()
      const permute = (arr: string[], start: number) => {
        if (start === arr.length - 1) {
          set.add(arr.join(''))
          return
        }
        for (let i = start; i < arr.length; i++) {
          const next = [...arr]
          ;[next[start], next[i]] = [next[i], next[start]]
          permute(next, start + 1)
        }
      }
      permute(chars, 0)
      return Array.from(set)
    }
    if (prizeCode === 'tod2reverse' && num.length === 2) {
      return [num, num[1] + num[0]]
    }
    return [num]
  }

  // นำเลขชุดที่เลือกไปใส่ในรายการเดิมพัน (ตรงกับ prizeTypeCode / ตารางรางวัล)
  const handleApplyNumberSet = (set: CustomerNumberSet) => {
    if (!addBetItem) {
      toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', { icon: '⚠️', position: 'top-center' })
      return
    }
    let added = 0
    for (const item of set.items || []) {
      const prizeCode = prizeTypeCodeToPrizeCodeMap[item.prizeTypeCode]
      if (!prizeCode) {
        toast(`ประเภท "${item.typeLabel}" ยังไม่รองรับในรอบนี้`, { icon: '⚠️', position: 'top-center' })
        continue
      }
      const numbers = item.numbers
        .split(/[,،\s]+/)
        .map(s => s.trim())
        .filter(Boolean)
      for (const num of numbers) {
        const toAdd = expandReverseNumbers(num, prizeCode)
        for (const displayNum of toAdd) {
          const multiplier = getPayoutRate(prizeCode, displayNum)
          if (multiplier > 0) {
            addBetItem({
              number: displayNum,
              betType: prizeCode,
              betTypeLabel: item.typeLabel,
              amount: 1,
              multiplier,
              discount: getDiscount(prizeCode),
            })
            added += 1
          }
        }
      }
    }
    setShowNumberSetModal(false)
    if (added > 0) toast.success(`เพิ่ม ${added} รายการจากเลขชุด "${set.name}"`)
    else toast('ไม่มีรายการที่เพิ่มได้จากเลขชุดนี้', { icon: '⚠️' })
  }

  const flagImage = useMemo(() => {
    if (typeof roundImage === 'string' && roundImage.trim() !== '') {
      return roundImage
    }

    if (!round) return 'logo.png'

    const explicitImage =
      (round as any).huayImageURL ||
      (round as any).huayImage ||
      (round as any).huayIcon ||
      (round as any).huayTypeIcon ||
      (round as any).huayTypeImageURL

    if (typeof explicitImage === 'string' && explicitImage.trim() !== '') {
      return explicitImage
    }

    const name = `${round.huayName || ''} ${round.huayTypeName || ''}`.toLowerCase()

    if (name.includes('ไทย') || name.includes('ออมสิน') || name.includes('ธกส') || name.includes('ธ.ก.ส.')) return 'flag/th.png'
    if (name.includes('ลาว')) return 'flag/la.png'
    if (name.includes('เวียดนาม') || name.includes('ฮานอย')) return 'flag/vn.png'
    if (name.includes('จีน')) return 'flag/cn.png'
    if (name.includes('ดาวน์โจนส์')) return 'flag/us.png'
    if (name.includes('นิเคอิ') || name.includes('ญี่ปุ่น')) return 'flag/jp.png'
    if (name.includes('ฮั่งเส็ง')) return 'flag/hk.png'
    if (name.includes('สิงคโปร์')) return 'flag/sg.png'
    if (name.includes('เยอรมัน')) return 'flag/de.png'
    if (name.includes('อังกฤษ')) return 'flag/uk.png'
    return 'logo.png'
  }, [round, roundImage])

  // Refs สำหรับ input fields
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Map prizeTypeCode from API to internal prizeCode
  const prizeTypeCodeToPrizeCodeMap: { [key: string]: string } = {
    '3_DIGIT_TOP': 'top3',
    '3_DIGIT_BOTTOM': 'down3',
    '3_DIGIT_TOD': 'tod3',
    '3_DIGIT_REVERSE': 'tod3reverse',
    '2_DIGIT_TOP': 'top2',
    '2_DIGIT_BOTTOM': 'down2',
    '2_DIGIT_TOD': 'tod2',
    '2_DIGIT_REVERSE': 'tod2reverse',
    'RUNNING_TOP': 'top1',
    'RUNNING_BOTTOM': 'down1',
    '4_DIGIT_TOP': 'top4',
    '4_DIGIT_TOD': 'tod4',
    '5_DIGIT_TOD': 'tod5',
    'FIXED_POSITION_ONES': 'puk1',
    'FIXED_POSITION_TENS': 'puk10',
    'FIXED_POSITION_HUNDREDS': 'puk100',
    // Add more mappings as needed
  }

  // Build reward map from selected package prizeConfigs
  const rewardMap = useMemo(() => {
    const map: { [key: string]: number } = {}
    
    if (selectedPackage?.prizeConfigs && selectedPackage.prizeConfigs.length > 0) {
      selectedPackage.prizeConfigs.forEach((config) => {
        // Only include active configs with valid payout rate
        const isActive = (config as any).isActive !== false
        if (isActive && config.payoutRate > 0) {
          const prizeCode = prizeTypeCodeToPrizeCodeMap[config.prizeTypeCode]
          if (prizeCode) {
            map[prizeCode] = config.payoutRate
            // Also map reverse codes if applicable
            if (config.prizeTypeCode === '3_DIGIT_TOP') {
              map['top3same'] = config.payoutRate // ตอง uses same rate as 3 ตัวบน
            }
            // Map fixed position digits to pick number group
            if (['FIXED_POSITION_ONES', 'FIXED_POSITION_TENS', 'FIXED_POSITION_HUNDREDS'].includes(config.prizeTypeCode)) {
              map['top3pick1group'] = config.payoutRate // เลขปัก uses same rate
            }
            // Map 4-5 digit types to 4-5 group
            if (['4_DIGIT_TOP', '4_DIGIT_TOD', '5_DIGIT_TOD'].includes(config.prizeTypeCode)) {
              map['top5fourgroup'] = config.payoutRate // 4-5 ตัว uses same rate
            }
            // ชุดกลับเลข: ใช้อัตราจ่ายจาก TOD หรือ TOP ถ้า package ไม่มี REVERSE แยก
            if (config.prizeTypeCode === '3_DIGIT_TOD') {
              map['tod3reverse'] = config.payoutRate
            }
            if (config.prizeTypeCode === '3_DIGIT_TOP' && map['tod3reverse'] === undefined) {
              map['tod3reverse'] = config.payoutRate
            }
            if (config.prizeTypeCode === '2_DIGIT_TOD') {
              map['tod2reverse'] = config.payoutRate
            }
            if (config.prizeTypeCode === '2_DIGIT_TOP' && map['tod2reverse'] === undefined) {
              map['tod2reverse'] = config.payoutRate
            }
          }
        }
      })
    }
    
    // Fallback to default values if package doesn't have configs
    if (Object.keys(map).length === 0) {
      return {
    'top3': 1000,
    'down3': 1000,
    'tod3': 150,
    'tod3reverse': 150,
    'top2': 100,
    'down2': 100,
    'tod2': 13,
    'tod2reverse': 13,
    'top1': 3.2,
    'down1': 4.2,
    'top3same': 1000,
    'top3pick1group': 8,
    'top5fourgroup': 0
      }
    }
    
    return map
  }, [selectedPackage])

  // Get custom payout rate from round limits for a specific number
  const getCustomPayoutRate = (prizeTypeCode: string, number: string): number | null => {
    if (!round?.roundLimits?.prizeTypeLimits) return null
    
    const prizeTypeLimit = round.roundLimits.prizeTypeLimits.find(
      limit => limit.prizeTypeCode === prizeTypeCode
    )
    
    if (!prizeTypeLimit?.numberSettings) return null
    
    const numberSetting = prizeTypeLimit.numberSettings.find(
      ns => ns.number === number
    )
    
    if (numberSetting?.customPayoutRate !== null && numberSetting?.customPayoutRate !== undefined) {
      return numberSetting.customPayoutRate
    }
    
    return null
  }

  // Get payout rate for a prize code and number (checks custom payout rate first)
  const getPayoutRate = (prizeCode: string, number?: string): number => {
    // If number is provided, check for custom payout rate first
    if (number) {
      const prizeTypeCode = Object.keys(prizeTypeCodeToPrizeCodeMap).find(
        key => prizeTypeCodeToPrizeCodeMap[key] === prizeCode
      )
      
      if (prizeTypeCode) {
        const customPayoutRate = getCustomPayoutRate(prizeTypeCode, number)
        if (customPayoutRate !== null) {
          return customPayoutRate
        }
      }
    }
    
    // Fallback to default payout rate from package
    return rewardMap[prizeCode] || 0
  }

  // Get discount for a prize code
  const getDiscount = (prizeCode: string): number => {
    if (!selectedPackage?.prizeConfigs) return 0
    
    const prizeTypeCode = Object.keys(prizeTypeCodeToPrizeCodeMap).find(
      key => prizeTypeCodeToPrizeCodeMap[key] === prizeCode
    )
    
    if (prizeTypeCode) {
      const config = selectedPackage.prizeConfigs.find(
        c => c.prizeTypeCode === prizeTypeCode
      )
      return config?.discount || 0
    }
    
    return 0
  }

  // นำโพยเก่าที่เลือกมาใส่ในรายการเดิมพันปัจจุบัน (ใช้อัตราจ่าย/ส่วนลดของแพ็กเกจปัจจุบันเสมอ)
  const handleApplyOldSlip = async (slip: BetSlip) => {
    if (!addBetItem || !slip.betItems?.length) {
      setShowOldSlipModal(false)
      return
    }
    const slipPackageName = slip.packageName || slip.packageCode || 'โพยเก่า'
    const currentPackageName = selectedPackage?.name || selectedPackage?.code || 'ปัจจุบัน'
    const isDifferentPackage =
      slip.packageCode &&
      selectedPackage?.code &&
      slip.packageCode !== selectedPackage.code

    if (isDifferentPackage) {
      const result = await Swal.fire({
        title: 'โพยคนละแพ็กเกจ',
        html: `โพยนี้สร้างจากแพ็กเกจ <strong>${slipPackageName}</strong><br><br>ยืนยันว่าจะดึงโพยเก่ามาใช้หรือไม่?<br><br><span class="text-muted">อัตราจ่ายและส่วนลดจะใช้ตามแพ็กเกจ <strong>${currentPackageName}</strong> (ที่คุณเลือกอยู่) ไม่ใช่อัตราจ่ายจากโพยเก่า</span>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยันดึงโพย',
        cancelButtonText: 'ยกเลิก',
        confirmButtonColor: '#198754',
        cancelButtonColor: '#6c757d',
        reverseButtons: true
      })
      if (!result.isConfirmed) return
    }

    let added = 0
    for (const item of slip.betItems) {
      const prizeCode = prizeTypeCodeToPrizeCodeMap[item.prizeTypeCode]
      if (!prizeCode) continue
      // ใช้อัตราจ่ายของแพ็กเกจปัจจุบันเสมอ (ไม่ใช้ lockedPayoutRate จากโพยเก่า)
      const multiplier = getPayoutRate(prizeCode, item.number)
      addBetItem({
        number: item.number,
        betType: prizeCode,
        betTypeLabel: item.prizeTypeName || item.prizeTypeCode,
        amount: item.amount || 1,
        multiplier,
        discount: getDiscount(prizeCode),
      })
      added += 1
    }
    setShowOldSlipModal(false)
    if (added > 0) {
      const msg =
        isDifferentPackage
          ? `ดึงโพย ${slip.slipId || slip._id} จำนวน ${added} รายการ (อัตราจ่ายตามแพ็กเกจปัจจุบัน: ${currentPackageName})`
          : `ดึงโพย ${slip.slipId || slip._id} จำนวน ${added} รายการ`
      toast.success(msg)
    } else {
      toast('โพยนี้ไม่มีรายการที่นำมาใช้ได้', { icon: '⚠️', position: 'top-center' })
    }
  }

  // Mapping สำหรับแสดงชื่อประเภทหวย
  const prizeCodeNameMap: { [key: string]: string } = {
    'top3': '3 ตัวบน',
    'down3': '3 ตัวล่าง',
    'tod3': '3 ตัวโต๊ด',
    'tod3reverse': '3 ตัวกลับ',
    'top2': '2 ตัวบน',
    'down2': '2 ตัวล่าง',
    'tod2': '2 ตัวโต๊ด',
    'tod2reverse': '2 ตัวกลับ',
    'top1': 'วิ่งบน',
    'down1': 'วิ่งล่าง',
    'top3same': 'ตอง',
    'top3pick1group': 'เลขปัก',
    'top5fourgroup': '4-5 ตัว'
  }

  // กำหนดกลุ่มประเภทการแทง
  const group3 = ['top3', 'down3', 'tod3', 'tod3reverse']
  const group2 = ['top2', 'down2', 'tod2', 'tod2reverse']
  const groupRun = ['top1', 'down1']
  const groupPick = ['top3pick1group']
  const group45 = ['top5fourgroup']

  const getGroup = (code: string) => {
    if (['top3same', ...group3].includes(code)) return 'group3'
    if (group2.includes(code)) return 'group2'
    if (groupRun.includes(code)) return 'groupRun'
    if (groupPick.includes(code)) return 'groupPick'
    if (group45.includes(code)) return 'group45'
    return 'other'
  }

  // ตรวจสอบว่ามีการเลือกประเภทการแทงหรือไม่
  const hasPrizeSelected = selectedPrizeCodes.length > 0

  // ตรวจสอบว่ามีการเลือกกลุ่ม 3 ตัวหรือไม่
  const hasThreeDigitSelected = selectedPrizeCodes.some(code => 
    ['top3', 'down3', 'tod3', 'tod3reverse', 'top3same'].includes(code)
  )

  // ตรวจสอบว่ามีการเลือกกลุ่ม 2 ตัวหรือไม่
  const hasTwoDigitSelected = selectedPrizeCodes.some(code => 
    ['top2', 'down2', 'tod2', 'tod2reverse'].includes(code)
  )

  // ตรวจสอบว่ามีการเลือกเลขปักหรือไม่
  const isPickNumberSelected = selectedPrizeCodes.includes('top3pick1group')

  // ตรวจสอบว่ามีการเลือก 4-5 ตัวหรือไม่
  const is45GroupSelected = selectedPrizeCodes.includes('top5fourgroup')

  // กำหนด currentGroup จาก selectedPrizeCodes
  const currentGroup = selectedPrizeCodes.length
    ? getGroup(selectedPrizeCodes[0])
    : null

  // คำนวณจำนวน input fields ตามประเภทการแทง
  const inputCount = useMemo(() => {
    if (!selectedPrizeCodes.length) return 5

    const firstCode = selectedPrizeCodes[0]
    
    // วิ่งบน/วิ่งล่าง ใช้ 1 ช่อง
    if (['top1', 'down1'].includes(firstCode)) {
      return 1
    }
    
    // เลขปัก ใช้ 1 ช่อง
    if (firstCode === 'top3pick1group') {
      return 1
    }
    
    // 4-5 ตัว ใช้ 4 หรือ 5 ช่อง
    if (firstCode === 'top5fourgroup') {
      const selectedPick = selectedPickNumbers[0] || ''
      if (selectedPick === '5 ตัวโต๊ด') return 5
      return 4
    }
    
    // 2 ตัว ใช้ 2 ช่อง
    if (['top2', 'down2', 'tod2', 'tod2reverse'].includes(firstCode)) {
      return 2
    }
    
    // 3 ตัว ใช้ 3 ช่อง (default)
    return 3
  }, [selectedPrizeCodes, selectedPickNumbers])

  // สร้าง numbers array ตาม inputCount
  const displayNumbers = useMemo(() => {
    // ถ้าเป็น special2DigitType (รูดหน้า/หลัง/19 ประตู) ใช้ 1 ช่อง
    if (special2DigitType) {
      return ['']
    }
    
    // ใช้ inputCount ที่คำนวณไว้
    return Array.from({ length: inputCount }, () => '')
  }, [inputCount, special2DigitType])

  // ฟังก์ชันสำหรับจัดการการคลิกประเภทการแทง
  const handlePrizeClick = (prizeCode: string) => {
    const currentGroup = getGroup(prizeCode)
    const currentSelectedGroup = selectedPrizeCodes.length
      ? getGroup(selectedPrizeCodes[0])
      : null

    // เคลียร์ special2DigitType เมื่อเปลี่ยนประเภทการแทง (ยกเว้นกรณี toggle off)
    // แต่ถ้าเป็น toggle off ของ prizeCode เดิม ไม่ต้องเคลียร์
    if (special2DigitType && !selectedPrizeCodes.includes(prizeCode)) {
      setSpecial2DigitType(null)
    }

    // กรณีพิเศษ: เลขตอง
    if (prizeCode === 'top3same') {
      if (!addBetItem) {
        toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', {
          icon: '⚠️',
          position: 'top-center',
        })
        return
      }

      // สร้างเลขตอง (000, 111, 222, ..., 999)
      const tongNumbersList = Array.from(
        { length: 10 },
        (_, i) => `${i}${i}${i}`
      )

      // สร้างรายการเดิมพันสำหรับแต่ละเลขตอง
      const top3Discount = getDiscount('top3')
      
      tongNumbersList.forEach(number => {
        const top3PayoutRate = getPayoutRate('top3', number)
        addBetItem({
          number: number,
          betType: 'top3',
          betTypeLabel: '3 ตัวบน',
          amount: 1,
          multiplier: top3PayoutRate,
          discount: top3Discount,
        })
      })

      toast.success(
        `เพิ่มรายการเลขตอง (${tongNumbersList.join(', ')}) สำหรับ 3 ตัวบน จำนวน ${
          tongNumbersList.length
        } รายการ เรียบร้อยแล้ว`,
        {
          position: 'top-center',
          duration: 3000,
        }
      )

      return
    }

    // ถ้าเป็น groupPick หรือ group45
    if (['groupPick', 'group45'].includes(currentGroup)) {
      // เคลียร์ special2DigitType เมื่อเปลี่ยนไป groupPick หรือ group45
      if (special2DigitType) {
        setSpecial2DigitType(null)
      }
      const isSameGroup = selectedPrizeCodes.includes(prizeCode)
      if (isSameGroup) {
        setSelectedPrizeCodes([])
        setSelectedPickNumbers([])
      } else {
        setSelectedPrizeCodes([prizeCode])
        setSelectedPickNumbers([])
        // Focus ไปที่ input ช่องแรกสำหรับ groupPick
        if (currentGroup === 'groupPick') {
          setTimeout(() => {
            if (inputRefs.current[0]) {
              inputRefs.current[0].focus()
            }
          }, 100)
        }
      }
      return
    }

    // ถ้าข้ามกลุ่ม → reset
    if (currentSelectedGroup && currentGroup !== currentSelectedGroup) {
      // เคลียร์ special2DigitType เมื่อข้ามกลุ่ม
      if (special2DigitType) {
        setSpecial2DigitType(null)
      }
      // ตรวจ reverse
      if (prizeCode === 'tod3reverse') {
        setSelectedPrizeCodes(['top3', 'tod3reverse'])
      } else if (prizeCode === 'tod2reverse') {
        setSelectedPrizeCodes(['top2', 'tod2reverse'])
      } else {
        setSelectedPrizeCodes([prizeCode])
      }
      setSelectedPickNumbers([])
      // Focus ไปที่ input ช่องแรก
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus()
        }
      }, 100)
      return
    }

    // toggle off
    if (selectedPrizeCodes.includes(prizeCode)) {
      setSelectedPrizeCodes(prev => prev.filter(code => code !== prizeCode))
      return
    }

    // เพิ่ม reverse พิเศษเมื่อเริ่มจาก 0
    if (
      selectedPrizeCodes.length === 0 &&
      (prizeCode === 'tod3reverse' || prizeCode === 'tod2reverse')
    ) {
      // เคลียร์ special2DigitType เมื่อเพิ่ม reverse
      if (special2DigitType) {
        setSpecial2DigitType(null)
      }
      const base = prizeCode === 'tod3reverse' ? 'top3' : 'top2'
      setSelectedPrizeCodes([base, prizeCode])
      // Focus ไปที่ input ช่องแรก
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus()
        }
      }, 100)
      return
    }

    // เพิ่มปกติ
    // เคลียร์ special2DigitType เมื่อเพิ่มประเภทใหม่ (ถ้ายังไม่ได้เลือกอยู่แล้ว)
    if (special2DigitType && !selectedPrizeCodes.includes(prizeCode)) {
      setSpecial2DigitType(null)
    }
    setSelectedPrizeCodes(prev => [...new Set([...prev, prizeCode])])
    // Focus ไปที่ input ช่องแรกหลังจากเลือกประเภท
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }, 100)
  }

  // ฟังก์ชันสำหรับจัดการการคลิกตัวเลือกพิเศษ (เลขปัก, 4-5 ตัว)
  const handlePickNumberClick = (label: string) => {
    // toggle ถ้าเลือกอยู่แล้ว → เอาออก
    if (selectedPickNumbers.includes(label)) {
      setSelectedPickNumbers(prev => prev.filter(item => item !== label))
      return
    }

    // ถ้าจะเลือก "5 ตัวโต๊ด" → ล้าง "4 ตัวบน" และ "4 ตัวโต๊ด"
    if (label === '5 ตัวโต๊ด') {
      setSelectedPickNumbers(['5 ตัวโต๊ด'])
      return
    }

    // ถ้า "5 ตัวโต๊ด" ถูกเลือกไว้แล้ว แล้วเลือก "4 ตัวบน/โต๊ด" → ล้างก่อน
    if (
      selectedPickNumbers.includes('5 ตัวโต๊ด') &&
      ['4 ตัวบน', '4 ตัวโต๊ด'].includes(label)
    ) {
      setSelectedPickNumbers([label])
      return
    }

    // กรณีปกติ เพิ่ม label เข้าไป
    setSelectedPickNumbers(prev => [...prev, label])
  }

  // ฟังก์ชันสำหรับจัดการการคลิกตัวเลข
  const handleNumberClick = (num: number) => {
    if (!hasPrizeSelected) {
      toast('กรุณาเลือกประเภทการแทงก่อน', {
        position: 'top-center',
        duration: 3000,
        icon: '⚠️'
      })
      return
    }

    // ตรวจสอบว่าเลือกเลขปักแต่ยังไม่เลือกตำแหน่ง
    if (isPickNumberSelected && selectedPickNumbers.length === 0) {
      toast('กรุณาเลือกตำแหน่งการปัก (ปักหลักร้อย, ปักหลักสิบ, หรือ ปักหลักหน่วย) ก่อนส่งโพย', {
        position: 'top-center',
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    // ตรวจสอบว่าเลือก 4-5 ตัวแต่ยังไม่เลือกประเภท
    if (is45GroupSelected && selectedPickNumbers.length === 0) {
      toast('กรุณาเลือกประเภทการแทง (4 ตัวบน, 4 ตัวโต๊ด, หรือ 5 ตัวโต๊ด) ก่อนส่งโพย', {
        position: 'top-center',
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    const numberStr = num.toString()

    // กรณีพิเศษ: รูดหน้า, รูดหลัง, 19 ประตู
    if (special2DigitType && inputRefs.current[0]) {
      inputRefs.current[0].value = numberStr
      inputRefs.current[0].focus()

      // ใช้ฟังก์ชันสำหรับสร้างรายการเดิมพัน
      createSpecial2DigitBetItems(numberStr, special2DigitType as 'front' | 'back' | '19door')

      // เคลียร์ input ทันที
      inputRefs.current[0].value = ''
      // Focus กลับไปที่ช่องแรก
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus()
        }
      }, 100)
      return
    }

    // กรณีทั่วไป: ใส่เลขใน input ช่องแรกที่ว่าง
    for (let i = 0; i < displayNumbers.length; i++) {
      const ref = inputRefs.current[i]
      if (ref && ref.value === '') {
        ref.value = numberStr

        // เลื่อนไปช่องถัดไป
        if (i + 1 < displayNumbers.length) {
          inputRefs.current[i + 1]?.focus()
        } else {
          // กรอกครบแล้ว ให้ตรวจสอบและส่งข้อมูล
          setTimeout(() => {
            checkAndSubmitBet()
            // เคลียร์ input ทั้งหมด
            inputRefs.current.forEach(ref => {
              if (ref) ref.value = ''
            })
            // Focus กลับไปที่ช่องแรก
            setTimeout(() => {
              if (inputRefs.current[0]) {
                inputRefs.current[0].focus()
              }
            }, 50)
          }, 100)
        }
        break
      }
    }
  }

  // ฟังก์ชันสำหรับจัดการการคลิกตัวเลือกพิเศษ 2 ตัว (รูดหน้า, รูดหลัง, 19 ประตู)
  const handleSpecial2DigitClick = (type: 'front' | 'back' | '19door') => {
    // ตรวจสอบว่าเลือกประเภท 2 ตัวหรือยัง
    if (!hasTwoDigitSelected) {
      toast('กรุณาเลือกประเภท 2 ตัวก่อน', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    setSpecial2DigitType(prev => (prev === type ? null : type))
    setSelectedPickNumbers([])

    if (type === 'front') {
      toast('รูดหน้า: พิมพ์เลข 1 หลัก จะได้เลข 2 ตัวที่ขึ้นต้นด้วยเลขนั้น', {
        icon: '⚠️',
        position: 'top-center',
        duration: 3000,
      })
    } else if (type === 'back') {
      toast('รูดหลัง: พิมพ์เลข 1 หลัก จะได้เลข 2 ตัวที่ลงท้ายด้วยเลขนั้น', {
        icon: '⚠️',
        position: 'top-center',
        duration: 3000,
      })
    } else if (type === '19door') {
      toast('19 ประตู: พิมพ์เลข 1 หลัก จะได้เลข 2 ตัวรูดหน้า+หลัง รวม 19 เลข', {
        icon: '⚠️',
        position: 'top-center',
        duration: 3000,
      })
    }

    // Focus ไปที่ input ช่องแรกสำหรับพิมพ์เลข
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }, 100)
  }

  // ฟังก์ชันสำหรับสร้างรายการเดิมพันพิเศษ 2 ตัว
  const createSpecial2DigitBetItems = (number: string, type: 'front' | 'back' | '19door') => {
    if (!addBetItem || !hasTwoDigitSelected) return

    // สร้างเลข 2 ตัวตามประเภท
    let numbers: string[] = []
    
    if (type === 'front') {
      // รูดหน้า: เลข 2 → 20, 21, 22, ..., 29
        numbers = Array.from({ length: 10 }, (_, i) => `${number}${i}`)
    } else if (type === 'back') {
      // รูดหลัง: เลข 2 → 02, 12, 22, ..., 92
        numbers = Array.from({ length: 10 }, (_, i) => `${i}${number}`)
    } else if (type === '19door') {
      // 19 ประตู: รวมรูดหน้าและหลัง แต่ตัดซ้ำออก
      const front = Array.from({ length: 10 }, (_, i) => `${number}${i}`)
      const back = Array.from({ length: 10 }, (_, i) => `${i}${number}`)
      const combined = [...front, ...back]
      numbers = Array.from(new Set(combined)).sort()
    }

    // ตรวจสอบว่ามี "2 ตัวกลับ" หรือไม่
    const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')

    // กรองประเภทที่ไม่ใช่ modifier
    const actualPrizeTypes = selectedPrizeCodes.filter(
      code => code !== 'tod2reverse'
    )

    let totalItemsAdded = 0

    // วนเลขแต่ละตัว
    numbers.forEach(num => {
      actualPrizeTypes.forEach(prizeCode => {
        const payoutRate = getPayoutRate(prizeCode, num)
        const discount = getDiscount(prizeCode)
        
        if (payoutRate <= 0) {
          console.warn(`⚠️ ไม่พบข้อมูลรางวัลสำหรับ: ${prizeCode}`)
          return
        }

        const betTypeLabel = prizeCodeNameMap[prizeCode] || prizeCode

        // ตรวจสอบว่าต้องสลับเลขหรือไม่
        const shouldReverse2 = hasReverse2 && !prizeCode.includes('tod')

        if (shouldReverse2) {
          // สร้างเลขสลับ (2 เลข สำหรับ 2 หลัก)
          const permutedNumbers = [num, num.split('').reverse().join('')]
          const uniquePermuted = Array.from(new Set(permutedNumbers))

          uniquePermuted.forEach(permutedNumber => {
            addBetItem({
              number: permutedNumber,
              betType: prizeCode,
              betTypeLabel: betTypeLabel,
              amount: 1,
              multiplier: payoutRate,
              discount: discount,
            })
            totalItemsAdded++
          })
        } else {
          // ประเภทโต๊ดหรือไม่มีการกลับ ใช้เลขเดิม 1 เลข
      addBetItem({
        number: num,
            betType: prizeCode,
            betTypeLabel: betTypeLabel,
        amount: 1,
            multiplier: payoutRate,
            discount: discount,
          })
          totalItemsAdded++
        }
      })
    })

    // แสดงข้อความแจ้งเตือน
    const typeLabel = type === 'front' ? 'รูดหน้า' : type === 'back' ? 'รูดหลัง' : '19 ประตู'
    const reverseText = hasReverse2 ? ' (รวมเลขสลับ)' : ''
    toast.success(
      `เพิ่มรายการ${typeLabel} เลข ${number} จำนวน ${totalItemsAdded} รายการ${reverseText} เรียบร้อยแล้ว`,
      {
      position: 'top-center',
        duration: 3000,
      }
    )

    // ล้าง input หลังใช้งาน
    inputRefs.current.forEach(ref => {
      if (ref) ref.value = ''
    })

    // Focus กลับไปที่ input ช่องแรก
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }, 100)
  }

  // ฟังก์ชันสำหรับสร้างรายการเดิมพัน 2 ตัว (สำหรับตัวเลือกเพิ่มเติม)
  const createTwoDigitBetItems = (numbers: string[], description: string) => {
    // ตรวจสอบว่าเลือกประเภท 2 ตัวหรือยัง
    if (!hasTwoDigitSelected) {
      toast('กรุณาเลือกประเภท 2 ตัวก่อน', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    if (!addBetItem || !numbers?.length) {
      toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    // ตรวจสอบว่ามี "2 ตัวกลับ" หรือไม่
    const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')

    // กรองประเภทที่ไม่ใช่ modifier
    const actualPrizeTypes = selectedPrizeCodes.filter(
      code => code !== 'tod2reverse'
    )

    let totalItemsAdded = 0

    // วนเลขแต่ละตัว
    numbers.forEach(number => {
      actualPrizeTypes.forEach(prizeCode => {
        const payoutRate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        
        if (payoutRate <= 0) {
          console.warn(`⚠️ ไม่พบข้อมูลรางวัลสำหรับ: ${prizeCode}`)
          return
        }

        const betTypeLabel = prizeCodeNameMap[prizeCode] || prizeCode

        // ตรวจสอบว่าต้องสลับเลขหรือไม่
        const shouldReverse2 = hasReverse2 && !prizeCode.includes('tod')

        if (shouldReverse2) {
          // สร้างเลขสลับ (2 เลข สำหรับ 2 หลัก)
          const permutedNumbers = [number, number.split('').reverse().join('')]
          const uniquePermuted = Array.from(new Set(permutedNumbers))

          uniquePermuted.forEach(permutedNumber => {
            addBetItem({
              number: permutedNumber,
              betType: prizeCode,
              betTypeLabel: betTypeLabel,
              amount: 1,
              multiplier: payoutRate,
              discount: discount,
            })
            totalItemsAdded++
          })
        } else {
          // ประเภทโต๊ดหรือไม่มีการกลับ ใช้เลขเดิม 1 เลข
          addBetItem({
            number: number,
            betType: prizeCode,
            betTypeLabel: betTypeLabel,
            amount: 1,
            multiplier: payoutRate,
            discount: discount,
          })
          totalItemsAdded++
        }
      })
    })

    // แสดงข้อความแจ้งเตือน
    const reverseText = hasReverse2 ? ' (รวมเลขสลับ)' : ''
    toast.success(
      `เพิ่มรายการ${description} จำนวน ${totalItemsAdded} รายการ${reverseText} เรียบร้อยแล้ว`,
      {
        position: 'top-center',
        duration: 3000,
      }
    )

    setSelectedNumbers(numbers)
  }

  // ฟังก์ชันสำหรับจัดการการคลิกช่วงเลข 3 ตัว
  const handleThreeDigitRangeClick = (range: string) => {
    // ตรวจสอบว่าเลือกประเภท 3 ตัวหรือยัง
    if (!hasThreeDigitSelected) {
      toast('กรุณาเลือกประเภท 3 ตัวก่อน', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    if (!addBetItem) {
      toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    // กำหนดช่วงเลข
    const getRangeNumbers = (rangeStr: string): string[] => {
      const numbers: string[] = []

      switch (rangeStr) {
        case '000-249':
          for (let i = 0; i <= 249; i++) {
            numbers.push(i.toString().padStart(3, '0'))
          }
          break
        case '250-499':
          for (let i = 250; i <= 499; i++) {
            numbers.push(i.toString().padStart(3, '0'))
          }
          break
        case '500-749':
          for (let i = 500; i <= 749; i++) {
            numbers.push(i.toString().padStart(3, '0'))
          }
          break
        case '750-999':
          for (let i = 750; i <= 999; i++) {
            numbers.push(i.toString().padStart(3, '0'))
          }
          break
        case '000-499':
          for (let i = 0; i <= 499; i++) {
            numbers.push(i.toString().padStart(3, '0'))
          }
          break
        case '500-999':
          for (let i = 500; i <= 999; i++) {
            numbers.push(i.toString().padStart(3, '0'))
          }
          break
        default:
          break
      }

      return numbers
    }

    const rangeNumbers = getRangeNumbers(range)

    if (rangeNumbers.length === 0) {
      toast('ไม่พบเลขในช่วงที่เลือก', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    // ตรวจสอบว่ามี "3 ตัวกลับ" หรือไม่
    const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')

    // กรองประเภทที่ไม่ใช่ modifier
    const actualPrizeTypes = selectedPrizeCodes.filter(
      code => code !== 'tod3reverse' && code !== 'top3same'
    )

    let totalItemsAdded = 0

    // วนเลขแต่ละตัวในช่วง
    rangeNumbers.forEach(number => {
      actualPrizeTypes.forEach(prizeCode => {
        const payoutRate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        
        if (payoutRate <= 0) {
          console.warn(`⚠️ ไม่พบข้อมูลรางวัลสำหรับ: ${prizeCode}`)
          return
        }

        const betTypeLabel = prizeCodeNameMap[prizeCode] || prizeCode

        // ตรวจสอบว่าต้องสลับเลขหรือไม่
        const shouldReverse3 = hasReverse3 && !prizeCode.includes('tod')

        if (shouldReverse3) {
          // สร้างเลขสลับ (6 เลข สำหรับ 3 หลัก)
          const chars = number.split('')
          const permutations = new Set<string>()
          
          // สร้าง permutations สำหรับ 3 หลัก
          const permute = (arr: string[], start: number = 0): void => {
            if (start === arr.length - 1) {
              permutations.add(arr.join(''))
              return
            }
            for (let i = start; i < arr.length; i++) {
              const newArr = [...arr]
              ;[newArr[start], newArr[i]] = [newArr[i], newArr[start]]
              permute(newArr, start + 1)
            }
          }
          permute(chars)

          Array.from(permutations).forEach(permutedNumber => {
            // สำหรับเลขสลับ ใช้ custom payout rate ของเลขเดิม (number) ถ้ามี
            const permutedPayoutRate = getPayoutRate(prizeCode, permutedNumber)
            addBetItem({
              number: permutedNumber,
              betType: prizeCode,
              betTypeLabel: betTypeLabel,
              amount: 1,
              multiplier: permutedPayoutRate,
              discount: discount,
            })
            totalItemsAdded++
          })
        } else {
          // ประเภทโต๊ดหรือไม่มีการกลับ ใช้เลขเดิม 1 เลข
          addBetItem({
            number: number,
            betType: prizeCode,
            betTypeLabel: betTypeLabel,
            amount: 1,
            multiplier: payoutRate,
            discount: discount,
          })
          totalItemsAdded++
        }
      })
    })

    // แสดงข้อความแจ้งเตือน
    const reverseText = hasReverse3 ? ' (รวมเลขสลับ)' : ''
    toast.success(
      `เพิ่มรายการช่วง ${range} จำนวน ${totalItemsAdded} รายการ${reverseText} เรียบร้อยแล้ว`,
      {
        position: 'top-center',
        duration: 3000,
      }
    )
  }

  // ฟังก์ชันสำหรับตัวเลือกเพิ่มเติม 2 ตัว
  const handleDoubleDigitClick = () => {
    const doubles = Array.from({ length: 10 }, (_, i) =>
      `${i}${i}`.padStart(2, '0')
    )
    createTwoDigitBetItems(doubles, 'เลขเบิ้ล')
    setSelectedNumbers(doubles)
    setSpecial2DigitType(null)
  }

  const handleLow2DigitClick = () => {
    const numbers = Array.from({ length: 50 }, (_, i) =>
      i.toString().padStart(2, '0')
    )
    createTwoDigitBetItems(numbers, 'สองตัวต่ำ (00-49)')
    setSelectedNumbers(numbers)
  }

  const handleHigh2DigitClick = () => {
    const numbers = Array.from({ length: 50 }, (_, i) =>
      (i + 50).toString().padStart(2, '0')
    )
    createTwoDigitBetItems(numbers, 'สองตัวสูง (50-99)')
    setSelectedNumbers(numbers)
  }

  const handleEven2DigitClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 0 && parseInt(str[1]) % 2 === 0) {
        numbers.push(str)
      }
    }
    createTwoDigitBetItems(numbers, 'สองตัวคู่')
    setSelectedNumbers(numbers)
  }

  const handleOdd2DigitClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 1 && parseInt(str[1]) % 2 === 1) {
        numbers.push(str)
      }
    }
    createTwoDigitBetItems(numbers, 'สองตัวคี่')
    setSelectedNumbers(numbers)
  }

  const handleSiblingNumberClick = () => {
    const numbers: string[] = []
    for (let ten = 0; ten <= 8; ten++) {
      const unit = ten + 1
      const str = `${ten}${unit}`
      numbers.push(str)
    }
    createTwoDigitBetItems(numbers, 'พี่น้อง')
    setSelectedNumbers(numbers)
  }

  const handleYoungerSiblingClick = () => {
    const numbers: string[] = []
    for (let unit = 1; unit <= 9; unit++) {
      const ten = unit - 1
      const str = `${unit}${ten}`
      numbers.push(str)
    }
    createTwoDigitBetItems(numbers, 'น้องพี่')
    setSelectedNumbers(numbers)
  }

  const handleOddOddClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 1 && parseInt(str[1]) % 2 === 1) {
        numbers.push(str)
      }
    }
    createTwoDigitBetItems(numbers, 'เลขคี่คี่')
    setSelectedNumbers(numbers)
  }

  const handleEvenEvenClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 0 && parseInt(str[1]) % 2 === 0) {
        numbers.push(str)
      }
    }
    createTwoDigitBetItems(numbers, 'เลขคู่คู่')
    setSelectedNumbers(numbers)
  }

  const handleEvenOddClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 0 && parseInt(str[1]) % 2 === 1) {
        numbers.push(str)
      }
    }
    createTwoDigitBetItems(numbers, 'เลขคู่คี่')
    setSelectedNumbers(numbers)
  }

  const handleAll2DigitClick = () => {
    const numbers = Array.from({ length: 100 }, (_, i) =>
      i.toString().padStart(2, '0')
    )
    createTwoDigitBetItems(numbers, 'เลข 00-99')
    setSelectedNumbers(numbers)
  }

  // ฟังก์ชันสำหรับตรวจสอบและส่งข้อมูลเดิมพัน
  // ฟังก์ชันสำหรับสลับเลข (Permutation)
  const generatePermutations = React.useCallback((numberStr: string): string[] => {
    const chars = numberStr.split('')
    const permutations = new Set<string>()

    if (numberStr.length === 2) {
      // สำหรับ 2 หลัก เช่น 25 → [25, 52]
      permutations.add(numberStr) // เลขเดิม
      permutations.add(chars[1] + chars[0]) // เลขกลับ
    } else if (numberStr.length === 3) {
      // สำหรับ 3 หลัก เช่น 236 → [236, 263, 326, 362, 623, 632]
      const permute = (arr: string[], start: number = 0) => {
        if (start === arr.length - 1) {
          permutations.add(arr.join(''))
          return
        }

        for (let i = start; i < arr.length; i++) {
          ;[arr[start], arr[i]] = [arr[i], arr[start]] // swap
          permute([...arr], start + 1)
          ;[arr[start], arr[i]] = [arr[i], arr[start]] // swap back
        }
      }

      permute(chars)
    } else {
      // ความยาวอื่นๆ ใช้เลขเดิม
      permutations.add(numberStr)
    }

    return Array.from(permutations).sort()
  }, [])

  const checkAndSubmitBet = () => {
    if (!addBetItem) return

    const inputValues = inputRefs.current
      .map(ref => ref?.value || '')
      .filter(value => value !== '')

    if (inputValues.length === 0) return

    const number = inputValues.join('')

    // ตรวจสอบว่าเลือกเลขปักแต่ยังไม่เลือกตำแหน่ง
    if (isPickNumberSelected && selectedPickNumbers.length === 0) {
      toast('กรุณาเลือกตำแหน่งการปัก (ปักหลักร้อย, ปักหลักสิบ, หรือ ปักหลักหน่วย) ก่อนส่งโพย', {
        position: 'top-center',
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    // ตรวจสอบว่าเลือก 4-5 ตัวแต่ยังไม่เลือกประเภท
    if (is45GroupSelected && selectedPickNumbers.length === 0) {
      toast('กรุณาเลือกประเภทการแทง (4 ตัวบน, 4 ตัวโต๊ด, หรือ 5 ตัวโต๊ด) ก่อนส่งโพย', {
        position: 'top-center',
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    // จัดการประเภทพิเศษ (เลขปัก, 4-5 ตัว)
    if (isPickNumberSelected && selectedPickNumbers.length > 0) {
      selectedPickNumbers.forEach(pickType => {
        if (['ปักหลักร้อย', 'ปักหลักสิบ', 'ปักหลักหน่วย'].includes(pickType)) {
          const prizeCodeMap: { [key: string]: string } = {
            'ปักหลักร้อย': 'FIXED_POSITION_HUNDREDS',
            'ปักหลักสิบ': 'FIXED_POSITION_TENS',
            'ปักหลักหน่วย': 'FIXED_POSITION_ONES',
          }
          const prizeTypeCode = prizeCodeMap[pickType]
          const prizeCode = prizeTypeCodeToPrizeCodeMap[prizeTypeCode] || prizeTypeCode
          const payoutRate = getPayoutRate(prizeCode, number)
          const discount = getDiscount(prizeCode)
          
          if (payoutRate > 0) {
            addBetItem({
              number: number,
              betType: prizeCode,
              betTypeLabel: pickType,
              amount: 1,
              multiplier: payoutRate,
              discount: discount,
            })
          }
        }
      })
    } else if (is45GroupSelected && selectedPickNumbers.length > 0) {
      selectedPickNumbers.forEach(pickType => {
        if (['4 ตัวบน', '4 ตัวโต๊ด', '5 ตัวโต๊ด'].includes(pickType)) {
          const prizeCodeMap: { [key: string]: string } = {
            '4 ตัวบน': '4_DIGIT_TOP',
            '4 ตัวโต๊ด': '4_DIGIT_TOD',
            '5 ตัวโต๊ด': '5_DIGIT_TOD',
          }
          const prizeTypeCode = prizeCodeMap[pickType]
          const prizeCode = prizeTypeCodeToPrizeCodeMap[prizeTypeCode] || prizeTypeCode
          const payoutRate = getPayoutRate(prizeCode, number)
          const discount = getDiscount(prizeCode)
          
          if (payoutRate > 0) {
            addBetItem({
              number: number,
              betType: prizeCode,
              betTypeLabel: pickType,
              amount: 1,
              multiplier: payoutRate,
              discount: discount,
            })
          }
        }
      })
    } else {
      // ตรวจสอบว่ามี "3 ตัวกลับ" หรือ "2 ตัวกลับ" หรือไม่
      const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')
      const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')

      // กรองประเภทที่ไม่ใช่ modifier (เพราะมันเป็น modifier)
      const actualPrizeTypes = selectedPrizeCodes.filter(
        code => code !== 'tod3reverse' && code !== 'tod2reverse' && code !== 'top3same'
      )

      let totalItemsAdded = 0

    // สร้างรายการเดิมพันสำหรับแต่ละประเภทที่เลือก
      actualPrizeTypes.forEach(prizeCode => {
        const payoutRate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        
        if (payoutRate > 0) {
          // ตรวจสอบว่าต้องสลับเลขหรือไม่
          // สำหรับ 3 ตัว: ถ้ามี tod3reverse และไม่ใช่ tod3 → สลับ
          // สำหรับ 2 ตัว: ถ้ามี tod2reverse และไม่ใช่ tod2 → สลับ
          const shouldReverse3 = hasReverse3 && !prizeCode.includes('tod') && number.length === 3
          const shouldReverse2 = hasReverse2 && !prizeCode.includes('tod') && number.length === 2

          if (shouldReverse3 || shouldReverse2) {
            // สร้างเลขสลับ
            const permutedNumbers = generatePermutations(number)

            permutedNumbers.forEach((permutedNumber: string) => {
              // สำหรับเลขสลับ ใช้ custom payout rate ของเลขสลับ (permutedNumber) ถ้ามี
              const permutedPayoutRate = getPayoutRate(prizeCode, permutedNumber)
              addBetItem({
                number: permutedNumber,
                betType: prizeCode,
                betTypeLabel: prizeCodeNameMap[prizeCode] || prizeCode,
                amount: 1,
                multiplier: permutedPayoutRate,
                discount: discount,
              })
              totalItemsAdded++
            })
          } else {
            // ประเภทโต๊ดหรือไม่มีการกลับ ใช้เลขเดิม 1 เลข
      addBetItem({
        number: number,
        betType: prizeCode,
        betTypeLabel: prizeCodeNameMap[prizeCode] || prizeCode,
        amount: 1,
              multiplier: payoutRate,
              discount: discount,
            })
            totalItemsAdded++
          }
        }
      })

      // แสดงข้อความแจ้งเตือน
      const reverseText = (hasReverse3 || hasReverse2) ? ' (รวมเลขสลับ)' : ''
      if (totalItemsAdded > 0) {
        toast.success(`เพิ่มรายการเดิมพัน ${number} จำนวน ${totalItemsAdded} รายการ${reverseText} เรียบร้อยแล้ว`, {
          position: 'top-center',
          duration: 2000,
        })
      }
    }

    // เคลียร์ input
    inputRefs.current.forEach(ref => {
      if (ref) ref.value = ''
    })

    // Focus กลับไปที่ช่องแรก
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }, 50)
  }

  // ฟังก์ชันสำหรับสร้างรายการเดิมพันจากแผงตัวเลข
  const handleSubmitFromPanel = () => {
    if (!addBetItem || selectedNumbers.length === 0) {
      toast('กรุณาเลือกตัวเลขจากแผงก่อน', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    if (selectedPrizeCodes.length === 0) {
      toast('กรุณาเลือกประเภทการแทงก่อน', {
        icon: '⚠️',
        position: 'top-center',
      })
      return
    }

    // ตรวจสอบว่ามี "2 ตัวกลับ" หรือ "3 ตัวกลับ" หรือไม่
    const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
    const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')

    // กรองประเภทที่ไม่ใช่ modifier
    const actualPrizeTypes = selectedPrizeCodes.filter(
      code => code !== 'tod2reverse' && code !== 'tod3reverse' && code !== 'top3same'
    )

    let totalItemsAdded = 0

    // วนเลขแต่ละตัวที่เลือก
    selectedNumbers.forEach(number => {
      actualPrizeTypes.forEach(prizeCode => {
        const payoutRate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        
        if (payoutRate <= 0) {
          console.warn(`⚠️ ไม่พบข้อมูลรางวัลสำหรับ: ${prizeCode}`)
          return
        }

        const betTypeLabel = prizeCodeNameMap[prizeCode] || prizeCode

        // ตรวจสอบว่าต้องสลับเลขหรือไม่
        const shouldReverse = (hasReverse2 && number.length === 2 && !prizeCode.includes('tod')) ||
                             (hasReverse3 && number.length === 3 && !prizeCode.includes('tod'))

        if (shouldReverse) {
          // สร้างเลขสลับ
          const chars = number.split('')
          const permutations = new Set<string>()
          
          if (number.length === 2) {
            permutations.add(number)
            permutations.add(chars[1] + chars[0])
          } else if (number.length === 3) {
            const permute = (arr: string[], start: number = 0): void => {
              if (start === arr.length - 1) {
                permutations.add(arr.join(''))
                return
              }
              for (let i = start; i < arr.length; i++) {
                const newArr = [...arr]
                ;[newArr[start], newArr[i]] = [newArr[i], newArr[start]]
                permute(newArr, start + 1)
              }
            }
            permute(chars)
          }

          Array.from(permutations).forEach(permutedNumber => {
            // สำหรับเลขสลับ ใช้ custom payout rate ของเลขสลับ (permutedNumber) ถ้ามี
            const permutedPayoutRate = getPayoutRate(prizeCode, permutedNumber)
            addBetItem({
              number: permutedNumber,
              betType: prizeCode,
              betTypeLabel: betTypeLabel,
              amount: 1,
              multiplier: permutedPayoutRate,
              discount: discount,
            })
            totalItemsAdded++
          })
        } else {
          // ไม่มีการกลับ ใช้เลขเดิม
          addBetItem({
            number: number,
            betType: prizeCode,
            betTypeLabel: betTypeLabel,
            amount: 1,
            multiplier: payoutRate,
            discount: discount,
          })
          totalItemsAdded++
        }
      })
    })

    // แสดงข้อความแจ้งเตือน
    const reverseText = (hasReverse2 || hasReverse3) ? ' (รวมเลขสลับ)' : ''
    toast.success(
      `เพิ่มรายการจากแผง จำนวน ${totalItemsAdded} รายการ${reverseText} เรียบร้อยแล้ว`,
      {
        position: 'top-center',
        duration: 3000,
      }
    )

    // เคลียร์ selectedNumbers และปิดแผง
    setSelectedNumbers([])
    setShowNumberPanel(false)
    setActiveTab(null) // Reset activeTab
    
    // เคลียร์ input และ focus กลับไปที่ช่องแรก (เหมือนกับ checkAndSubmitBet)
    inputRefs.current.forEach(ref => {
      if (ref) ref.value = ''
    })
    
    // Focus กลับไปที่ช่องแรก
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    }, 50)
  }

  // NumberPanel Component
  const NumberPanel: React.FC<{
    group: string | null
    numbers: string[]
    tabStep: number | null
    activeTab: number | null
    setActiveTab: (tab: number | null) => void
  }> = ({ group: _group, numbers, tabStep, activeTab, setActiveTab }) => {
    const tabValuePrefix = (value: number) => {
      if (tabStep === 100) {
        return value.toString().padStart(3, '0').slice(0, 1)
      } else if (tabStep === 10) {
        return value.toString().padStart(2, '0').slice(0, 1)
      } else if (tabStep === 1000) {
        return value.toString().padStart(4, '0').slice(0, 1)
      } else if (tabStep === 10000) {
        return value.toString().padStart(5, '0').slice(0, 1)
      }
      return ''
    }

    const filteredNumbers = useMemo(() => {
      if (tabStep == null || activeTab == null) return numbers
      return numbers.filter(num => num.startsWith(tabValuePrefix(activeTab)))
    }, [numbers, tabStep, activeTab])

    const tabs = useMemo(() => {
      if (!tabStep) return []
      return Array.from(
        new Set(
          numbers.map(n => Math.floor(parseInt(n, 10) / tabStep) * tabStep)
        )
      ).sort((a, b) => a - b)
    }, [numbers, tabStep])

    return (
      <>
        {tabs.length > 0 && (
          <div className="row px-2 mb-2">
            {tabs.map(tab => (
              <div
                key={tab}
                className={`nav-link col px-1 py-0 ${
                  activeTab === tab ? 'active' : ''
                }`}
                onClick={() => setActiveTab(tab)}
              >
                <button className="check-button w-100 bgc-4 c-dark d-block">
                  <p>
                    {tabStep === 100
                      ? tab.toString().padStart(3, '0')
                      : tabStep === 10
                      ? tab.toString().padStart(2, '0')
                      : tabStep === 1000
                      ? tab.toString().padStart(4, '0')
                      : tabStep === 10000
                      ? tab.toString().padStart(5, '0')
                      : tab.toString()}
                  </p>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="num-list">
          {filteredNumbers.map(num => (
            <div key={num} className="num-item">
              <button
                className={`button-num ${
                  selectedNumbers.includes(num) || selectedNumbersFromBetItems.has(num) ? 'active' : ''
                }`}
                onClick={() => {
                  // เมื่อกดเลขจากแผง ให้ส่งไปยัง bet slip ทันที (เหมือนกับกรอก input)
                  if (!addBetItem || selectedPrizeCodes.length === 0) {
                    toast('กรุณาเลือกประเภทการแทงก่อน', {
                      position: 'top-center',
                      duration: 3000,
                      icon: '⚠️'
                    })
                    return
                  }

                  // ตรวจสอบว่าเลือกเลขปักแต่ยังไม่เลือกตำแหน่ง
                  if (isPickNumberSelected && selectedPickNumbers.length === 0) {
                    toast('กรุณาเลือกตำแหน่งการปัก (ปักหลักร้อย, ปักหลักสิบ, หรือ ปักหลักหน่วย) ก่อนส่งโพย', {
                      position: 'top-center',
                      duration: 4000,
                      icon: '⚠️'
                    })
                    return
                  }

                  // ตรวจสอบว่าเลือก 4-5 ตัวแต่ยังไม่เลือกประเภท
                  if (is45GroupSelected && selectedPickNumbers.length === 0) {
                    toast('กรุณาเลือกประเภทการแทง (4 ตัวบน, 4 ตัวโต๊ด, หรือ 5 ตัวโต๊ด) ก่อนส่งโพย', {
                      position: 'top-center',
                      duration: 4000,
                      icon: '⚠️'
                    })
                    return
                  }

                  // ตรวจสอบว่ามี "2 ตัวกลับ" หรือ "3 ตัวกลับ" หรือไม่
                  const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
                  const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')

                  // กรองประเภทที่ไม่ใช่ modifier
                  const actualPrizeTypes = selectedPrizeCodes.filter(
                    code => code !== 'tod2reverse' && code !== 'tod3reverse' && code !== 'top3same'
                  )

                  let totalItemsAdded = 0

                  // สร้าง bet items สำหรับเลขที่กด
                  actualPrizeTypes.forEach(prizeCode => {
                    const payoutRate = getPayoutRate(prizeCode)
                    const discount = getDiscount(prizeCode)
                    
                    if (payoutRate <= 0) {
                      console.warn(`⚠️ ไม่พบข้อมูลรางวัลสำหรับ: ${prizeCode}`)
                      return
                    }

                    const betTypeLabel = prizeCodeNameMap[prizeCode] || prizeCode

                    // ตรวจสอบว่าต้องสลับเลขหรือไม่
                    const shouldReverse = (hasReverse2 && num.length === 2 && !prizeCode.includes('tod')) ||
                                         (hasReverse3 && num.length === 3 && !prizeCode.includes('tod'))

                    if (shouldReverse) {
                      // สร้างเลขสลับ
                      const chars = num.split('')
                      const permutations = new Set<string>()
                      
                      if (num.length === 2) {
                        permutations.add(num)
                        permutations.add(chars[1] + chars[0])
                      } else if (num.length === 3) {
                        const permute = (arr: string[], start: number = 0): void => {
                          if (start === arr.length - 1) {
                            permutations.add(arr.join(''))
                            return
                          }
                          for (let i = start; i < arr.length; i++) {
                            const newArr = [...arr]
                            ;[newArr[start], newArr[i]] = [newArr[i], newArr[start]]
                            permute(newArr, start + 1)
                          }
                        }
                        permute(chars)
                      }

                      Array.from(permutations).forEach(permutedNumber => {
                        addBetItem({
                          number: permutedNumber,
                          betType: prizeCode,
                          betTypeLabel: betTypeLabel,
                          amount: 1,
                          multiplier: payoutRate,
                          discount: discount,
                        })
                        totalItemsAdded++
                      })
                    } else {
                      // ไม่มีการกลับ ใช้เลขเดิม
                      addBetItem({
                        number: num,
                        betType: prizeCode,
                        betTypeLabel: betTypeLabel,
                        amount: 1,
                        multiplier: payoutRate,
                        discount: discount,
                      })
                      totalItemsAdded++
                    }
                  })

                  // แสดงข้อความแจ้งเตือน (ถ้ามีการเพิ่มรายการ)
                  if (totalItemsAdded > 0) {
                    const reverseText = (hasReverse2 || hasReverse3) ? ' (รวมเลขสลับ)' : ''
                    toast.success(
                      `เพิ่มรายการ ${num} จำนวน ${totalItemsAdded} รายการ${reverseText} เรียบร้อยแล้ว`,
                      {
                        position: 'top-center',
                        duration: 2000,
                      }
                    )

                    // เคลียร์ input และ focus กลับไปที่ช่องแรก (เหมือนกับ checkAndSubmitBet)
                    inputRefs.current.forEach(ref => {
                      if (ref) ref.value = ''
                    })
                    
                    // Focus กลับไปที่ช่องแรก
                    setTimeout(() => {
                      if (inputRefs.current[0]) {
                        inputRefs.current[0].focus()
                      }
                    }, 50)
                  }
                }}
              >
                {num}
              </button>
            </div>
          ))}
        </div>
      </>
    )
  }

  // ฟังก์ชันสำหรับลบตัวเลขสุดท้าย
  const handleClearLastDigit = () => {
    for (let i = inputRefs.current.length - 1; i >= 0; i--) {
      const ref = inputRefs.current[i]
      if (ref?.value) {
        ref.value = ''
        ref.focus()
        break
      }
    }
  }

  // ฟังก์ชันสำหรับแสดงสรุปการเลือก
  const renderSelectedSummary = () => {
    if (!selectedPrizeCodes.length) {
      return <h6 className="c-yellow">-- กรุณาเลือกหวยที่จะแทง --</h6>
    }

    return (
      <ul className="select-list">
        {selectedPrizeCodes.map(code => {
          const displayName = prizeCodeNameMap[code] || code
          return (
            <li key={code}>
              <small className="bgc-4 text-center c-white px-2 bdr-5 c-dark">
                {displayName}
              </small>
            </li>
          )
        })}
        {selectedPickNumbers.map(label => (
          <li key={label}>
            <small className="bgc-1 text-center c-white px-2 bdr-5 c-dark">
              {label}
            </small>
          </li>
        ))}
      </ul>
    )
  }

  // Helper function to get prize code from name (moved outside useMemo)
  const getPrizeCodeFromName = (name: string): string => {
    const nameToCodeMap: { [key: string]: string } = {
      '3 ตัวบน': 'top3',
      '3 ตัวล่าง': 'down3',
      '3 ตัวโต๊ด': 'tod3',
      '3 ตัวกลับ': 'tod3reverse',
      '2 ตัวบน': 'top2',
      '2 ตัวล่าง': 'down2',
      '2 ตัวโต๊ด': 'tod2',
      '2 ตัวกลับ': 'tod2reverse',
      'วิ่งบน': 'top1',
      'วิ่งล่าง': 'down1',
      'ตอง': 'top3same',
      'เลขปัก': 'top3pick1group',
      '4-5 ตัว': 'top5fourgroup'
    }
    return nameToCodeMap[name] || name
  }

  // Build betTypes from selected package or use defaults
  const betTypes = useMemo(() => {
    const defaultTypes = [
    { name: '3 ตัวบน', payout: 1000, color: 'bgg-4' },
    { name: '3 ตัวโต๊ด', payout: 150, color: 'bgg-4' },
    { name: '3 ตัวกลับ', payout: 0, color: 'bgg-4', icon: 'fas fa-sync' },
    { name: '2 ตัวบน', payout: 100, color: 'bgg-3' },
    { name: '2 ตัวล่าง', payout: 100, color: 'bgg-3' },
      { name: '2 ตัวกลับ', payout: 0, color: 'bgg-4', icon: 'fas fa-sync' },
    { name: '2 ตัวโต๊ด', payout: 13, color: 'bgg-3' },
    { name: '3 ตัวล่าง', payout: 0, color: 'bgc-13', disabled: true },
    { name: 'ตอง', payout: 1000, color: 'bgg-4' }
  ]

    if (!selectedPackage?.prizeConfigs || selectedPackage.prizeConfigs.length === 0) {
      return defaultTypes
    }

    // Map prize configs to bet types
    return defaultTypes.map(betType => {
      const prizeCode = getPrizeCodeFromName(betType.name)
      const payoutRate = rewardMap[prizeCode] || 0
      
      // Update payout and remove disabled flag if we have a valid rate from package
      if (payoutRate > 0) {
        const { disabled, ...rest } = betType
        // For 3 ตัวล่าง, change color to bgg-3 when enabled (like 2 ตัวโต๊ด)
        const color = betType.name === '3 ตัวล่าง' ? 'bgg-3' : rest.color
        return { ...rest, payout: payoutRate, color }
      }
      
      // If no payout rate, keep disabled state for 3 ตัวล่าง with bgc-13 color
      // But keep original color for 3 ตัวกลับ and 2 ตัวกลับ (they have icons)
      if (betType.name === '3 ตัวล่าง' && payoutRate === 0) {
        return { ...betType, disabled: true, color: 'bgc-13' }
      }
      
      // Keep original color for reverse types (they have icons and should use bgg-4 like ตอง)
      if (betType.name === '3 ตัวกลับ' || betType.name === '2 ตัวกลับ') {
        return { ...betType, color: 'bgg-4' } // Use bgg-4 like ตอง
      }
      
      return betType
    })
  }, [selectedPackage, rewardMap])

  const lastRowBetTypes = useMemo(() => {
    const defaultTypes = [
    { name: 'วิ่งบน', payout: 3.2, color: 'bgg-1', icon: undefined },
    { name: 'วิ่งล่าง', payout: 4.2, color: 'bgg-1', icon: undefined },
    { name: 'เลขปัก', payout: 0, color: 'bgg-1', icon: undefined },
    { name: '4-5 ตัว', payout: 0, color: 'bgg-1', icon: undefined }
  ]

    if (!selectedPackage?.prizeConfigs || selectedPackage.prizeConfigs.length === 0) {
      return defaultTypes
    }

    return defaultTypes.map(betType => {
      const prizeCode = getPrizeCodeFromName(betType.name)
      const payoutRate = rewardMap[prizeCode] || 0
      
      if (payoutRate > 0) {
        return { ...betType, payout: payoutRate }
      }
      
      return betType
    })
  }, [selectedPackage, rewardMap])

  return (
    <div className="col-12 col-lg-6 px-1 mb-2">
      <div className="">
        <div className="bdr-5 bgc-4 c-dark px-2 py-1 wrap justify-content-between mb-2">
          <div className="wrap">
            <div className="icon-f">
              <img
                src={/^https?:\/\//i.test(flagImage) ? flagImage : getImageUrl(flagImage)}
                alt={round?.huayName || 'หวย'}
              />
            </div>
            <p className="mx-2 t-medium c-red">{round?.huayName || 'เลือกหวย'}</p>
          </div>
          <div className="bgc-9 bdr-5 px-2 c-white wrap justify-content-center">
            <div className="text-center">
              <p className="mb-0">{countdown}</p>
              {closeTime && (
                // <small style={{ fontSize: '0.7rem' }}>ปิดรับ {closeTime}</small>
                <small style={{ fontSize: '0.7rem' }}></small>
              )}
            </div>
          </div>
        </div>

        <div className="row mx--2">
          {betTypes.map((bet, index) => {
            const prizeCode = getPrizeCodeFromName(bet.name)
            const isSelected = selectedPrizeCodes.includes(prizeCode)
            const payoutRate = getPayoutRate(prizeCode)
            const hasReward = payoutRate > 0
            const isEnabled = !bet.disabled && hasReward
            
            // For 3 ตัวกลับ and 2 ตัวกลับ, they should not be disabled (they have icons)
            const isReverseType = bet.name === '3 ตัวกลับ' || bet.name === '2 ตัวกลับ'
            const shouldBeEnabled = isReverseType ? true : isEnabled
            
            return (
              <div key={index} className="col-4 px--2 mb-1">
                <button
                  className={`w-100 hover-dashed bdr-5 ${isSelected ? 'active-dashed' : ''}`}
                  onClick={() => handlePrizeClick(prizeCode)}
                  disabled={!shouldBeEnabled}
                  style={{
                    opacity: !shouldBeEnabled ? 0.5 : 1,
                    filter: !shouldBeEnabled ? 'grayscale(50%)' : 'none',
                  }}
                >
                  <div className={`check-button justify-content-start ps-1 ${
                    // For 3 ตัวกลับ and 2 ตัวกลับ, always use bgg-4 (like ตอง)
                    isReverseType 
                      ? 'bgg-4' 
                      : (isEnabled ? bet.color : 'bgc-13')
                  } c-white`}>
                    <p className="me-2">{bet.name}</p>
                    {bet.icon ? (
                      <small className="cycle-b c-dark">
                        <i className={bet.icon}></i>
                      </small>
                    ) : (
                      <small className={`cycle-b ${bet.payout > 100 ? 'c-green' : bet.payout > 10 ? 'c-blue' : 'c-red'}`}>
                        {hasReward ? payoutRate.toLocaleString('th-TH') : '-'}
                      </small>
                    )}
                  </div>
                </button>
              </div>
            )
          })}
        </div>

        <div className="row mx--2">
          {lastRowBetTypes.map((bet, index) => {
            const prizeCode = getPrizeCodeFromName(bet.name)
            const payoutRate = getPayoutRate(prizeCode)
            const hasReward = payoutRate > 0
            const isEnabled = hasReward
            const isSelected = selectedPrizeCodes.includes(prizeCode)
            
            return (
            <div key={index} className="col-3 px--2 mb-1">
              <button
                className={`w-100 hover-dashed bdr-5 ${isSelected ? 'active-dashed' : ''}`}
                onClick={() => handlePrizeClick(prizeCode)}
                  disabled={!isEnabled}
                  style={{
                    opacity: !isEnabled ? 0.5 : 1,
                    filter: !isEnabled ? 'grayscale(50%)' : 'none',
                  }}
              >
                  <div className={`check-button ${bet.name === 'เลขปัก' || bet.name === '4-5 ตัว' ? 'justify-content-center' : 'justify-content-start'} ps-1 ${isEnabled ? bet.color : 'bgc-13'} c-white`}>
                  <p className={bet.name === 'เลขปัก' || bet.name === '4-5 ตัว' ? 'me-0' : 'me-2'}>{bet.name}</p>
                  {bet.icon ? (
                    <small className="cycle-b c-dark">
                      <i className={bet.icon}></i>
                    </small>
                    ) : hasReward ? (
                      <small className={`cycle-b ${payoutRate > 100 ? 'c-green' : payoutRate > 10 ? 'c-blue' : 'c-red'}`}>
                        {payoutRate.toLocaleString('th-TH')}
                    </small>
                    ) : (
                      <small className="cycle-b c-dark">-</small>
                    )}
                </div>
              </button>
            </div>
            )
          })}
        </div>

        {/* ตัวเลือกเพิ่มเติม 3 ตัว - แสดงเฉพาะเมื่อเลือกกลุ่ม 3 ตัว */}
        {hasThreeDigitSelected && (
        <div className="row mx--1">
          <div className="col-12 px-1 my-2">
            <h6 className="text-center t-regular">
              ตัวเลือกเพิ่มเติม 3 ตัว
            </h6>
          </div>

          {['000-249', '250-499', '500-749', '750-999'].map((range, index) => (
            <div key={index} className="col-3 px--2 mb-1">
                <button 
                  className="w-100 hover-dashed bdr-5"
                  onClick={() => handleThreeDigitRangeClick(range)}
                >
                <div className="check-button justify-content-center ps-1 bgg-3 c-white">
                  <p className="me-0">{range}</p>
                </div>
              </button>
            </div>
          ))}

          {['000-499', '500-999'].map((range, index) => (
            <div key={index} className="col-6 px--2 mb-1">
                <button 
                  className="w-100 hover-dashed bdr-5"
                  onClick={() => handleThreeDigitRangeClick(range)}
                >
                <div className="check-button justify-content-center ps-1 bgg-1 c-white">
                  <p className="me-0">{range}</p>
                </div>
              </button>
            </div>
          ))}
        </div>
        )}

        {/* ตัวเลือกเพิ่มเติม 2 ตัว - แสดงเฉพาะเมื่อเลือกกลุ่ม 2 ตัว */}
        {hasTwoDigitSelected && (
        <div className="row mx--1">
          <div className="col-12 px-1 my-2">
            <h6 className="text-center t-regular">
              ตัวเลือกเพิ่มเติม 2 ตัว
            </h6>
          </div>

            {['รูดหน้า', 'รูดหลัง', '19 ประตู'].map((option, index) => {
              const type = option === 'รูดหน้า' ? 'front' : option === 'รูดหลัง' ? 'back' : '19door'
              const isActive = special2DigitType === type
              
              return (
            <div key={index} className="col-3 px--2 mb-1">
                  <button 
                    className={`w-100 hover-dashed bdr-5 ${isActive ? 'active-dashed' : ''}`}
                    onClick={() => handleSpecial2DigitClick(type)}
                  >
                    <div className={`check-button justify-content-center ps-1 ${isActive ? 'bgg-4' : 'bgg-1'} c-white`}>
                  <p className="me-0">{option}</p>
                </div>
              </button>
            </div>
              )
            })}

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleDoubleDigitClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-3 c-white">
                  <p className="me-0">เลขเบิ้ล</p>
                </div>
              </button>
            </div>

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleLow2DigitClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-4 c-white">
                  <p className="me-0">สองตัวต่ำ</p>
                </div>
              </button>
            </div>

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleHigh2DigitClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-4 c-white">
                  <p className="me-0">สองตัวสูง</p>
                </div>
              </button>
            </div>

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleEven2DigitClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-3 c-white">
                  <p className="me-0">สองตัวคู่</p>
                </div>
              </button>
            </div>

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleOdd2DigitClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-3 c-white">
                  <p className="me-0">สองตัวคี่</p>
                </div>
              </button>
            </div>

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleSiblingNumberClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-4 c-white">
                  <p className="me-0">พี่น้อง</p>
                </div>
              </button>
            </div>

            <div className="col-3 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleYoungerSiblingClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-4 c-white">
                  <p className="me-0">น้องพี่</p>
                </div>
              </button>
            </div>

            {['เลขคี่คี่', 'เลขคู่คู่'].map((option, index) => {
              const handler = option === 'เลขคี่คี่' ? handleOddOddClick : handleEvenEvenClick
              
              return (
            <div key={index} className="col-3 px--2 mb-1">
                  <button 
                    className="w-100 hover-dashed bdr-5"
                    onClick={handler}
                  >
                <div className="check-button justify-content-center ps-1 bgg-5 c-white">
                  <p className="me-0">{option}</p>
                </div>
              </button>
            </div>
              )
            })}

            <div className="col-6 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleEvenOddClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-1 c-white">
                  <p className="me-0">เลขคู่คี่</p>
                </div>
              </button>
            </div>

            <div className="col-6 px--2 mb-1">
              <button 
                className="w-100 hover-dashed bdr-5"
                onClick={handleAll2DigitClick}
              >
                <div className="check-button justify-content-center ps-1 bgg-1 c-white">
                  <p className="me-0">เลข 00-99</p>
        </div>
              </button>
            </div>
          </div>
        )}

        {/* ตัวเลือกเพิ่มเติม เลขปัก - แสดงเฉพาะเมื่อเลือกเลขปัก */}
        {isPickNumberSelected && (
        <div className="row mx--1">
          <div className="col-12 px-1 my-2">
            <h6 className="text-center t-regular">
              ตัวเลือกเพิ่มเติม เลขปัก
            </h6>
          </div>

            {['ปักหลักร้อย', 'ปักหลักสิบ', 'ปักหลักหน่วย'].map((option, index) => {
              const isSelected = selectedPickNumbers.includes(option)
              
              return (
            <div key={index} className="col-4 px--2 mb-1">
                  <button 
                    className={`w-100 hover-dashed bdr-5 ${isSelected ? 'active-dashed' : ''}`}
                    onClick={() => handlePickNumberClick(option)}
                  >
                <div className="check-button justify-content-start ps-1 bgg-1 c-white">
                  <p className="me-2">{option}</p>
                  <small className="cycle-b c-red">8</small>
                </div>
              </button>
            </div>
              )
            })}
        </div>
        )}

        {/* ตัวเลือกเพิ่มเติม 4-5 ตัว - แสดงเฉพาะเมื่อเลือก 4-5 ตัว */}
        {is45GroupSelected && (
        <div className="row mx--1">
          <div className="col-12 px-1 my-2">
            <h6 className="text-center t-regular">
              ตัวเลือกเพิ่มเติม 4-5 ตัว
            </h6>
          </div>

            {['4 ตัวบน', '4 ตัวโต๊ด', '5 ตัวโต๊ด'].map((option, index) => {
              const isSelected = selectedPickNumbers.includes(option)
              
              return (
            <div key={index} className="col-4 px--2 mb-1">
                  <button 
                    className={`w-100 hover-dashed bdr-5 ${isSelected ? 'active-dashed' : ''}`}
                    onClick={() => handlePickNumberClick(option)}
                  >
                <div className="check-button justify-content-center ps-1 bgg-1 c-white">
                  <p className="me-0">{option}</p>
                </div>
              </button>
            </div>
              )
            })}
        </div>
        )}

        <div className="row mx--1 justify-content-center">
          <div className="col-12 px--2 mb-2">
            <div className="bgc-3 text-center c-white px-2 py-1 bdr-5">
              <p>รายการที่เลือก</p>
              {renderSelectedSummary()}
            </div>
          </div>

          <div className="col-12 px--2 mb-3">
            <h4 className="text-center t-medium">
              ระบุตัวเลข
              {special2DigitType && (
                <span className="c-orange">
                  {' - '}
                  {special2DigitType === 'front' && 'รูดหน้า'}
                  {special2DigitType === 'back' && 'รูดหลัง'}
                  {special2DigitType === '19door' && '19 ประตู'}
                  <button
                    className="btn btn-sm btn-outline-warning ms-2"
                    onClick={() => setSpecial2DigitType(null)}
                    title="ยกเลิกโหมดพิเศษ"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              )}
            </h4>
            <ul className="list-number">
              {displayNumbers.map((_, index) => (
                <li key={`number-${index}`}>
                  <div className="inp-num bdr-5">
                    <input 
                      ref={(el) => {
                        if (el) {
                        inputRefs.current[index] = el
                        }
                      }}
                      type="text" 
                      id={`number${index + 1}`}
                      maxLength={1}
                      pattern="[0-9]*"
                      inputMode="numeric"
                      disabled={!hasPrizeSelected}
                      onInput={(e) => {
                        const val = (e.target as HTMLInputElement).value
                        const onlyDigit = val.replace(/[^0-9]/g, '').slice(0, 1)
                        ;(e.target as HTMLInputElement).value = onlyDigit

                        // ถ้าเป็น special2DigitType และกรอกเลขแล้ว
                        if (special2DigitType && onlyDigit && index === 0) {
                          // ใช้ฟังก์ชันสำหรับสร้างรายการเดิมพัน
                          createSpecial2DigitBetItems(onlyDigit, special2DigitType as 'front' | 'back' | '19door')
                          // เคลียร์ input ทันที
                          ;(e.target as HTMLInputElement).value = ''
                          // Focus กลับไปที่ช่องแรก
                          setTimeout(() => {
                            inputRefs.current[0]?.focus()
                          }, 100)
                          return
                        }

                        if (onlyDigit && index + 1 < displayNumbers.length) {
                          // เลื่อนไปช่องถัดไป
                          inputRefs.current[index + 1]?.focus()
                        } else if (onlyDigit && index + 1 === displayNumbers.length) {
                          // กรอกครบช่องสุดท้ายแล้ว ให้ตรวจสอบและส่งข้อมูล
                          setTimeout(() => {
                            checkAndSubmitBet()
                            // เคลียร์ input ทั้งหมด
                            inputRefs.current.forEach(ref => {
                              if (ref) ref.value = ''
                            })
                            // Focus กลับไปที่ช่องแรก
                            setTimeout(() => {
                              inputRefs.current[0]?.focus()
                            }, 50)
                          }, 100)
                        }
                      }}
                      onKeyPress={(e) => {
                        // ป้องกันการพิมพ์ตัวอักษร - อนุญาตเฉพาะตัวเลข 0-9
                        const char = String.fromCharCode(e.which)
                        if (!/[0-9]/.test(char)) {
                          e.preventDefault()
                          return false
                        }
                      }}
                      onKeyDown={(e) => {
                        // อนุญาตปุ่มพิเศษ เช่น Backspace, Delete, Tab, Arrow keys
                        const allowedKeys = [
                          'Backspace',
                          'Delete',
                          'Tab',
                          'Escape',
                          'Enter',
                          'ArrowLeft',
                          'ArrowRight',
                          'ArrowUp',
                          'ArrowDown',
                          'Home',
                          'End',
                        ]

                        // อนุญาต Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                        if (
                          e.ctrlKey &&
                          ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())
                        ) {
                          return
                        }

                        // ถ้าเป็นตัวเลข 0-9 ให้ผ่าน
                        if (/^[0-9]$/.test(e.key)) {
                          return
                        }

                        // ถ้าเป็นปุ่มที่อนุญาต ให้ผ่าน
                        if (allowedKeys.includes(e.key)) {
                          // จัดการ Backspace พิเศษ
                          if (
                            e.key === 'Backspace' &&
                            !(e.target as HTMLInputElement).value &&
                            index > 0
                          ) {
                            inputRefs.current[index - 1]?.focus()
                          }
                          // จัดการ Enter
                        if (e.key === 'Enter') {
                            // ตรวจสอบว่ามีเลขกรอกครบหรือยัง
                            const inputValues = inputRefs.current
                              .map(ref => ref?.value || '')
                              .filter(value => value !== '')
                            
                            if (inputValues.length === displayNumbers.length) {
                          checkAndSubmitBet()
                              // เคลียร์ input ทั้งหมด
                              inputRefs.current.forEach(ref => {
                                if (ref) ref.value = ''
                              })
                              // Focus กลับไปที่ช่องแรก
                              setTimeout(() => {
                                inputRefs.current[0]?.focus()
                              }, 50)
                            }
                          }
                          return
                        }

                        // บล็อคปุ่มอื่นๆ ทั้งหมด
                        e.preventDefault()
                        return false
                      }}
                      onPaste={(e) => {
                        // ป้องกันการ paste ข้อความที่ไม่ใช่ตัวเลข
                        e.preventDefault()
                        const paste = (e.clipboardData || (window as any).clipboardData).getData('text')
                        const onlyDigits = paste.replace(/[^0-9]/g, '')

                        if (onlyDigits.length > 0) {
                          // ใส่ตัวเลขในแต่ละช่องตามลำดับ
                          const digitsToFill = onlyDigits.slice(0, displayNumbers.length)
                          
                          digitsToFill.split('').forEach((digit, i) => {
                            const ref = inputRefs.current[index + i]
                            if (ref) {
                              ref.value = digit
                            }
                          })

                          // ถ้ากรอกครบทุกช่องแล้ว ให้ส่งข้อมูล
                          if (digitsToFill.length === displayNumbers.length) {
                            setTimeout(() => {
                              checkAndSubmitBet()
                              // เคลียร์ input ทั้งหมด
                              inputRefs.current.forEach(ref => {
                                if (ref) ref.value = ''
                              })
                              // Focus กลับไปที่ช่องแรก
                              setTimeout(() => {
                                if (inputRefs.current[0]) {
                                  inputRefs.current[0].focus()
                                }
                              }, 50)
                            }, 100)
                          } else {
                            // Focus ไปที่ช่องถัดไปที่ยังไม่ได้กรอก
                            const nextIndex = index + digitsToFill.length
                            if (nextIndex < displayNumbers.length && inputRefs.current[nextIndex]) {
                              inputRefs.current[nextIndex].focus()
                            }
                          }
                        }
                        return false
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-8 px-1">
            <div className="row mx--1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <div key={num} className="col-4 px-1 mb-2">
                  <button 
                    className="button-num"
                    onClick={() => handleNumberClick(num)}
                    disabled={!hasPrizeSelected}
                  >
                    {num}
                  </button>
                </div>
              ))}
              <div className="col-12 px-1 mb-2">
                <button 
                  className="button-num"
                  onClick={() => handleNumberClick(0)}
                  disabled={!hasPrizeSelected}
                >
                  0
                </button>
              </div>
            </div>
          </div>

          <div className="col-4 px-1">
            <div className="row mx--1">
              <div className="col-12 px-1 mb-2">
                <button
                  className="button-click c-white bgg-1 bdr-5"
                  onClick={handleClearLastDigit}
                >
                  <i className="fas fa-backspace"></i> ล้างตัวเลข
                </button>
              </div>

              <div className="col-12 px-1 mb-2">
                <button 
                  className="button-click c-white bgg-5 bdr-5"
                  onClick={() => setShowNumberSetModal(true)}
                >
                  เลือกเลขชุด
                </button>
              </div>

              <div className="col-12 px-1 mb-2">
                <button
                  type="button"
                  className="button-click c-white bgg-5 bdr-5"
                  onClick={() => setShowOldSlipModal(true)}
                >
                  ดึงโพยเก่า
                </button>
              </div>

              <div className="col-12 px-1 mb-2">
                <button 
                  className={`button-click bdr-5 ${
                    hasPrizeSelected && !isPickNumberSelected
                      ? 'c-white bgg-4'
                      : 'c-dark bgc-13'
                  }`}
                  disabled={!hasPrizeSelected || isPickNumberSelected || is45GroupSelected}
                  onClick={() => {
                    if (!hasPrizeSelected) {
                      toast('กรุณาเลือกประเภทการแทงก่อน', {
                        icon: '⚠️',
                        position: 'top-center',
                      })
                      return
                    }
                    if (isPickNumberSelected) {
                      toast('ประเภทนี้ไม่สามารถเลือกจากแผงได้', {
                        icon: '⚠️',
                        position: 'top-center',
                      })
                      return
                    }
                    if (is45GroupSelected) {
                      toast('ประเภท 4-5 ตัวไม่สามารถเลือกจากแผงได้ เนื่องจากเลขมีจำนวนมาก', {
                        icon: '⚠️',
                        position: 'top-center',
                      })
                      return
                    }
                    setShowNumberPanel(prev => {
                      const newValue = !prev
                      // เมื่อเปิดแผงครั้งแรก (group3 = 3 ตัว) ให้ set activeTab เป็น 0 (แสดง 000-099)
                      if (newValue && activeTab === null && currentGroup === 'group3') {
                        setActiveTab(0)
                      } else if (!newValue) {
                        // เมื่อปิดแผง ให้ reset activeTab
                        setActiveTab(null)
                      }
                      return newValue
                    })
                  }}
                  title={
                    isPickNumberSelected || is45GroupSelected
                      ? 'ประเภทนี้ไม่สามารถเลือกจากแผงได้'
                      : !hasPrizeSelected
                      ? 'กรุณาเลือกประเภทการแทงก่อน'
                      : ''
                  }
                >
                  {showNumberPanel ? 'ปิดแผง' : 'เลือกจากแผง'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* แผงตัวเลข - แสดงเฉพาะเมื่อเปิดแผงและมีการเลือกประเภทการแทง (ยกเว้น 4-5 ตัว) */}
        {showNumberPanel &&
          (currentGroup === 'group3' ||
            currentGroup === 'group2' ||
            currentGroup === 'groupRun') && (
        <div className="row mx--1">
          <div className="col-12 px-1 my-2">
            <h6 className="text-center t-regular">เลือกจากแผง</h6>
          </div>
              <NumberPanel
                group={currentGroup}
                numbers={
                  currentGroup === 'group3'
                    ? Array.from({ length: 1000 }, (_, i) =>
                        i.toString().padStart(3, '0')
                      )
                    : currentGroup === 'group2'
                    ? Array.from({ length: 100 }, (_, i) =>
                        i.toString().padStart(2, '0')
                      )
                    : currentGroup === 'groupRun'
                    ? Array.from({ length: 10 }, (_, i) => i.toString())
                    : []
                }
                tabStep={
                  currentGroup === 'group3'
                    ? 100
                    : currentGroup === 'group2'
                    ? 10
                    : null
                }
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
          <div className="col-12 px--2 mb-2">
                <button 
                  className="w-100 hover-dashed bdr-5"
                  onClick={handleSubmitFromPanel}
                >
              <div className="check-button justify-content-center bgg-1 c-white">
                    <p className="me-0">เลือกจากแผงเสร็จกดที่นี่เพื่อใส่ราคา</p>
              </div>
            </button>
          </div>
        </div>
          )}

        {/* Modal เลือกเลขชุด (responsive: mobile full-width, desktop 480px) */}
        {showNumberSetModal && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-75"
              style={{ zIndex: 1040 }}
              onClick={() => setShowNumberSetModal(false)}
              aria-hidden
            />
            <div
              className="position-fixed start-50 bg-white bdr-5 shadow overflow-auto"
              style={{
                zIndex: 1050,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(480px, calc(100vw - 24px))',
                maxHeight: 'calc(100vh - 24px)',
                maxWidth: 'calc(100vw - 24px)'
              }}
              role="dialog"
              aria-labelledby="SetNumerModalLabel"
              aria-hidden="false"
            >
              <div className="modal-header border-bottom flex-nowrap">
                <h5 className="modal-title t-medium c-red text-truncate me-2" id="SetNumerModalLabel">
                  เลือกเลขชุด
                </h5>
                <button
                  type="button"
                  className="btn-close rounded-circle flex-shrink-0"
                  style={{ backgroundColor: '#fff' }}
                  onClick={() => setShowNumberSetModal(false)}
                  aria-label="ปิด"
                />
              </div>
              <div className="modal-body p-0 overflow-auto">
                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table table-striped table-bordered text-center mb-0" style={{ minWidth: 280 }}>
                    <thead className="table-dark">
                      <tr>
                        <th>ลำดับ</th>
                        <th>ชื่อเลขชุด</th>
                        <th>เมื่อ</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingNumberSets ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3">
                            กำลังโหลด...
                          </td>
                        </tr>
                      ) : numberSetList.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3">
                            ยังไม่มีเลขชุด กรุณาไปสร้างที่หน้าจัดการเลขชุด
                          </td>
                        </tr>
                      ) : (
                        numberSetList.map((set, index) => (
                          <tr key={set._id}>
                            <td>{index + 1}.</td>
                            <td>{set.name}</td>
                            <td>
                              {set.updatedAt
                                ? new Date(set.updatedAt).toLocaleString('th-TH', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : '-'}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="border-0 bg-transparent c-red p-0"
                                onClick={() => handleApplyNumberSet(set)}
                              >
                                <p className="mb-0">คลิกเลือก</p>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer justify-content-center border-top">
                <button
                  type="button"
                  className="check-button bgg-2 bdc-4 c-dark bx-sd px-3"
                  onClick={() => setShowNumberSetModal(false)}
                >
                  ปิด
                </button>
              </div>
            </div>
          </>
        )}

        {/* Modal ดึงโพยเก่า — responsive: mobile เต็มจอ margin น้อย, desktop 720px */}
        {showOldSlipModal && (
          <>
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-75"
              style={{ zIndex: 1040 }}
              onClick={() => setShowOldSlipModal(false)}
              aria-hidden
            />
            <div
              className="position-fixed start-50 bg-white bdr-5 shadow overflow-auto"
              style={{
                zIndex: 1050,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'min(720px, calc(100vw - 24px))',
                maxHeight: 'calc(100vh - 24px)',
                maxWidth: 'calc(100vw - 24px)'
              }}
              role="dialog"
              aria-labelledby="OldListModalLabel"
              aria-hidden="false"
            >
              <div className="modal-header border-bottom flex-nowrap">
                <h5 className="modal-title t-medium c-red text-truncate me-2" id="OldListModalLabel">
                  ดึงโพยเก่า
                </h5>
                <button
                  type="button"
                  className="btn-close rounded-circle flex-shrink-0"
                  style={{ backgroundColor: '#fff' }}
                  onClick={() => setShowOldSlipModal(false)}
                  aria-label="ปิด"
                />
              </div>
              <div className="modal-body p-0 overflow-auto">
                {!loadingOldSlips && oldSlipList.length > 0 && selectedPackage?.code && (
                  <div className="old-slip-package-filter-wrap px-3 py-2 border-bottom d-flex align-items-center">
                    <label className="d-flex align-items-center gap-2 small mb-0 cursor-pointer text-dark">
                      <input
                        type="checkbox"
                        checked={showOnlyCurrentPackage}
                        onChange={(e) => setShowOnlyCurrentPackage(e.target.checked)}
                        className="old-slip-package-filter-checkbox"
                      />
                      <span>แสดงเฉพาะแพ็กเกจปัจจุบัน ({selectedPackage.name || selectedPackage.code})</span>
                    </label>
                  </div>
                )}
                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table table-striped table-bordered text-center mb-0" style={{ minWidth: 360 }}>
                    <thead className="table-dark">
                      <tr>
                        <th>ลำดับ</th>
                        <th>โพย</th>
                        <th>รอบ</th>
                        <th>แพ็กเกจ</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingOldSlips ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-3">
                            กำลังโหลด...
                          </td>
                        </tr>
                      ) : (() => {
                        const listToShow =
                          showOnlyCurrentPackage && selectedPackage?.code
                            ? oldSlipList.filter((s) => s.packageCode === selectedPackage.code)
                            : oldSlipList
                        if (listToShow.length === 0) {
                          return (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-3">
                                {showOnlyCurrentPackage && selectedPackage?.code
                                  ? 'ไม่มีโพยที่ตรงกับแพ็กเกจปัจจุบัน'
                                  : isYeekee
                                    ? 'ไม่มีโพยเก่า'
                                    : 'ไม่มีโพยเก่าในรอบนี้'}
                              </td>
                            </tr>
                          )
                        }
                        return listToShow.map((slip, index) => (
                          <tr key={slip._id || slip.slipId}>
                            <td>{index + 1}.</td>
                            <td>{slip.slipId || slip._id || '-'}</td>
                            <td>
                              {slip.huayName || slip.roundNumber || '-'}
                              {slip.createdAt
                                ? ` (${new Date(slip.createdAt).toLocaleString('th-TH', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })})`
                                : ''}
                            </td>
                            <td className="text-nowrap">{slip.packageName || slip.packageCode || '-'}</td>
                            <td>
                              <button
                                type="button"
                                className="check-button bgc-8 c-white w-100 border-0 bdr-5 py-1 px-2"
                                onClick={() => handleApplyOldSlip(slip)}
                              >
                                <p className="mb-0">เลือก</p>
                              </button>
                            </td>
                          </tr>
                        ))
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer justify-content-center border-top">
                <button
                  type="button"
                  className="check-button bgg-2 bdc-4 c-dark bx-sd px-3"
                  onClick={() => setShowOldSlipModal(false)}
                >
                  ปิด
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default LotteryBetSideLeft
